import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { DataState } from "../components/ui/DataState";
import { detectPii } from "../lib/pii";
import {
  reportTypeLabel,
  type AccessRequest,
  type AdminUserRow,
  type Report,
  type ReportActivity,
  URGENCY_LEVELS,
} from "../lib/types";

type Tab = "reports" | "access";

export default function AdminReview() {
  const [tab, setTab] = useState<Tab>("reports");
  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">Admin review</h1>
          <p className="mt-2 max-w-3xl text-slate-300">
            Review reports, approve alerts, and manage access requests.
            Approved alerts must remove personal identifying details.
          </p>
        </div>
        <div className="flex w-full gap-2 rounded-lg border border-white/10 bg-white/5 p-1 sm:w-auto">
          <TabButton active={tab === "reports"} onClick={() => setTab("reports")}>
            Reports
          </TabButton>
          <TabButton active={tab === "access"} onClick={() => setTab("access")}>
            Access requests
          </TabButton>
        </div>
      </header>

      {tab === "reports" ? <ReportsTab /> : <AccessTab />}
    </div>
  );
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium sm:flex-none ${active ? "bg-accent text-ink-950" : "text-slate-200 hover:bg-white/5"}`}
    >
      {children}
    </button>
  );
}

function ReportsTab() {
  const api = useApi();
  const [reports, setReports] = useState<Report[] | null>(null);
  const [verifiers, setVerifiers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        api.get<{ reports: Report[] }>("/api/admin/reports"),
        api.get<{ users: AdminUserRow[] }>(
          "/api/admin/users?role=trusted_verifier",
        ),
      ]);
      setReports(r.reports);
      setVerifiers(v.users.filter((u) => u.is_active === 1));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  return (
    <section className="mt-8">
      <DataState
        loading={loading}
        error={error}
        data={reports}
        empty={!!reports && reports.length === 0}
        loadingMessage="Loading reports…"
        emptyMessage="No reports yet."
      >
        {(items) => (
          <div className="space-y-5">
            {items.map((r) => (
              <ReportRow
                key={r.id}
                report={r}
                verifiers={verifiers}
                onChanged={load}
              />
            ))}
          </div>
        )}
      </DataState>
    </section>
  );
}

function ReportRow({
  report,
  verifiers,
  onChanged,
}: {
  report: Report;
  verifiers: AdminUserRow[];
  onChanged: () => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = async (status: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/admin/reports/${report.id}/status`, { status });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const assign = async (verifierId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/admin/reports/${report.id}/assign`, {
        verifier_user_id: verifierId === "" ? null : Number(verifierId),
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assignment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={urgencyTone(report.urgency)}>{report.urgency}</Badge>
        <Badge>{reportTypeLabel(report.report_type)}</Badge>
        {report.neighborhood && <Badge>{report.neighborhood}</Badge>}
        <Badge tone="warning">{report.status}</Badge>
        {report.anonymous ? (
          <Badge>anonymous</Badge>
        ) : (
          report.submitter_name && (
            <Badge>by {report.submitter_name}</Badge>
          )
        )}
      </div>
      <CardTitle className="mt-3 text-lg">
        {report.location ?? "Location not specified"}
      </CardTitle>
      <p className="mt-2 whitespace-pre-line text-sm text-slate-300">
        {report.description}
      </p>
      <div className="mt-2 text-xs text-slate-400">
        Submitted {report.created_at}
        {report.organization_name && ` · ${report.organization_name}`}
        {report.assigned_verifier_id
          ? ` · assigned to verifier #${report.assigned_verifier_id}`
          : ""}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Select
          label="Assign to verifier"
          value={String(report.assigned_verifier_id ?? "")}
          onChange={(e) => void assign(e.target.value)}
          disabled={busy || verifiers.length === 0}
          hint={
            verifiers.length === 0
              ? "No active verifiers. Promote a user from /admin/users."
              : undefined
          }
        >
          <option value="">— Unassigned —</option>
          {verifiers.map((v) => (
            <option key={v.id} value={v.id}>
              {v.full_name ?? v.email}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowAudit((s) => !s)}
        >
          {showAudit ? "Hide audit log" : "Audit log"}
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => update("needs_verification")}
        >
          Needs verification
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => update("rejected")}
        >
          Reject
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => update("resolved")}
        >
          Mark resolved
        </Button>
        <Button
          size="sm"
          disabled={busy}
          onClick={() => setShowAlert((s) => !s)}
        >
          {showAlert ? "Cancel alert draft" : "Approve as alert"}
        </Button>
      </div>

      {error && (
        <p className="mt-3 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {showAudit && <AuditLog reportId={report.id} />}

      {showAlert && (
        <PromoteToAlertForm
          report={report}
          onCreated={() => {
            setShowAlert(false);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

function AuditLog({ reportId }: { reportId: number }) {
  const api = useApi();
  const [items, setItems] = useState<ReportActivity[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ activity: ReportActivity[] }>(
        `/api/admin/reports/${reportId}/audit`,
      )
      .then((r) => {
        setItems(r.activity);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [api, reportId]);

  return (
    <div className="surface-soft mt-4 p-4">
      <div className="text-xs uppercase tracking-wider text-slate-400">
        Activity log
      </div>
      <div className="mt-2">
        <DataState
          loading={loading}
          error={error}
          data={items}
          empty={!!items && items.length === 0}
          loadingMessage="Loading activity…"
          emptyMessage="No recorded actions yet."
        >
          {(activities) => (
            <ol className="mt-1 grid gap-2 text-sm text-slate-300">
              {activities.map((a) => (
                <li key={a.id} className="flex flex-wrap gap-2">
                  <span className="text-slate-500">{a.created_at}</span>
                  <span className="text-white">
                    {a.action.replace(/_/g, " ")}
                  </span>
                  {a.previous_status && a.new_status && (
                    <span className="text-slate-400">
                      ({a.previous_status} → {a.new_status})
                    </span>
                  )}
                  <span className="text-slate-400">
                    by {a.actor_name ?? a.actor_email ?? "system"}
                  </span>
                  {a.note && (
                    <span className="basis-full pl-3 text-slate-400">
                      “{a.note}”
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </DataState>
      </div>
    </div>
  );
}

function PromoteToAlertForm({
  report,
  onCreated,
}: {
  report: Report;
  onCreated: () => void;
}) {
  const api = useApi();
  const [form, setForm] = useState({
    title: "",
    category: report.report_type,
    neighborhood: report.neighborhood ?? "",
    urgency: report.urgency,
    public_summary: "",
    recommended_action: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const set =
    (k: keyof typeof form) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setForm((s) => ({ ...s, [k]: e.target.value }));
      setAcknowledged(false);
    };

  const piiHits = detectPii(
    `${form.title}\n${form.public_summary}\n${form.recommended_action}`,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (piiHits.length > 0) {
      if (!acknowledged) {
        setError(
          "Possible personal info detected — confirm the override below to publish anyway.",
        );
        return;
      }
      if (!overrideReason.trim()) {
        setError(
          "Provide an override reason — it gets stored in the audit log alongside the alert.",
        );
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/admin/alerts", {
        ...form,
        report_id: report.id,
        override_reason:
          piiHits.length > 0 && overrideReason.trim()
            ? overrideReason.trim()
            : null,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create alert");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface-soft mt-5 grid gap-5 p-5">
      <div className="text-xs uppercase tracking-wider text-slate-400">
        Public-safe alert preview
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-white/5 bg-ink-900/50 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">
            Raw report (admin only)
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge tone={urgencyTone(report.urgency)}>{report.urgency}</Badge>
            <Badge>{reportTypeLabel(report.report_type)}</Badge>
            {report.neighborhood && <Badge>{report.neighborhood}</Badge>}
            {report.anonymous ? (
              <Badge>anonymous</Badge>
            ) : (
              report.submitter_name && (
                <Badge>by {report.submitter_name}</Badge>
              )
            )}
          </div>
          <p className="mt-3 whitespace-pre-line text-sm text-slate-300">
            {report.description}
          </p>
          <p className="mt-3 text-xs text-slate-400">
            {report.location ? `Location: ${report.location}` : null}
          </p>
        </div>

        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
          <div className="text-xs uppercase tracking-wider text-accent">
            What community will see
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge tone={urgencyTone(form.urgency)}>{form.urgency}</Badge>
            {form.category && (
              <Badge>{form.category.replace(/_/g, " ")}</Badge>
            )}
            {form.neighborhood && <Badge>{form.neighborhood}</Badge>}
          </div>
          <h4 className="mt-3 text-base font-semibold text-white">
            {form.title || "Add a public title"}
          </h4>
          <p className="mt-2 text-sm text-slate-300">
            {form.public_summary || "Add a public summary with no PII."}
          </p>
          {form.recommended_action && (
            <p className="mt-3 rounded-md border border-white/5 bg-white/[0.03] px-3 py-2 text-sm text-slate-300">
              <span className="font-semibold text-white">Recommended: </span>
              {form.recommended_action}
            </p>
          )}
        </div>
      </div>

      {piiHits.length > 0 && (
        <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4">
          <div className="text-sm font-semibold text-red-100">
            Possible personal info in the public-facing fields
          </div>
          <ul className="mt-2 grid gap-1 text-xs text-red-200">
            {piiHits.map((h, i) => (
              <li key={i}>
                <span className="text-red-100">{h.kind}</span>: “{h.match}”
              </li>
            ))}
          </ul>
          <label className="mt-3 flex items-center gap-2 text-sm text-red-100">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded border-red-300/40 bg-ink-900 text-red-300 focus:ring-red-300/30"
            />
            I've reviewed these and confirm they are safe to publish.
          </label>
          {acknowledged && (
            <div className="mt-3">
              <Textarea
                label="Override reason (logged in audit trail)"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                hint="Why is publishing this safe even though the PII detector flagged it? Stored on the activity log so future reviewers can audit the decision."
                rows={2}
              />
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Public alerts must remove names, addresses, plate numbers, or anything
        that identifies a private person.
      </p>
      <Input
        required
        label="Public title"
        name="title"
        value={form.title}
        onChange={set("title")}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <Input
          label="Category"
          name="category"
          value={form.category}
          onChange={set("category")}
        />
        <Input
          label="Neighborhood"
          name="neighborhood"
          value={form.neighborhood}
          onChange={set("neighborhood")}
        />
        <Select
          label="Urgency"
          name="urgency"
          value={form.urgency}
          onChange={set("urgency")}
        >
          {URGENCY_LEVELS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </Select>
      </div>
      <Textarea
        required
        label="Public summary (no PII)"
        name="public_summary"
        value={form.public_summary}
        onChange={set("public_summary")}
      />
      <Textarea
        label="Recommended action"
        name="recommended_action"
        value={form.recommended_action}
        onChange={set("recommended_action")}
      />
      {error && (
        <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Publishing…" : "Publish alert"}
        </Button>
      </div>
    </form>
  );
}

function AccessTab() {
  const api = useApi();
  const [items, setItems] = useState<AccessRequest[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    return api
      .get<{ access_requests: AccessRequest[] }>("/api/admin/access-requests")
      .then((r) => {
        setItems(r.access_requests);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const update = async (id: number, status: AccessRequest["status"]) => {
    await api.patch(`/api/admin/access-requests/${id}/status`, { status });
    void load();
  };

  return (
    <section className="mt-8">
      <DataState
        loading={loading}
        error={error}
        data={items}
        empty={!!items && items.length === 0}
        loadingMessage="Loading access requests…"
        emptyMessage="No access requests."
      >
        {(rows) => (
          <div className="space-y-5">
            {rows.map((r) => (
              <Card key={r.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={
                      r.status === "approved"
                        ? "success"
                        : r.status === "rejected"
                          ? "critical"
                          : "warning"
                    }
                  >
                    {r.status}
                  </Badge>
                  {r.neighborhood && <Badge>{r.neighborhood}</Badge>}
                  {r.city && <Badge>{r.city}</Badge>}
                </div>
                <CardTitle className="mt-3 text-lg">{r.full_name}</CardTitle>
                <p className="text-sm text-slate-300 break-all">{r.email}</p>
                <p className="mt-2 text-sm text-slate-300">
                  {r.community_role && (
                    <>
                      Role: <span className="text-white">{r.community_role}</span>
                      {" · "}
                    </>
                  )}
                  {r.organization_name && (
                    <>
                      Org:{" "}
                      <span className="text-white">{r.organization_name}</span>
                    </>
                  )}
                </p>
                {r.reason && (
                  <p className="mt-3 whitespace-pre-line text-sm text-slate-300">
                    {r.reason}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => update(r.id, "approved")}
                    disabled={r.status === "approved"}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => update(r.id, "rejected")}
                    disabled={r.status === "rejected"}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => update(r.id, "pending")}
                    disabled={r.status === "pending"}
                  >
                    Reset to pending
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DataState>
    </section>
  );
}
